import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getViewConfigByNameAdmin, upsertViewConfigAdmin } from '../services/viewConfigService';
import '../styles/adminAnnouncementBar.css';

const CONFIG_NAME = 'ANNOUNCEMENT_BAR';

const DEFAULT_CONFIG = {
  enabled: true,
  rotationInterval: 4000,
  showOnMobile: true,
  showOnDesktop: true,
  announcements: [],
};

const AdminAnnouncementBar = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving]     = useState(false);
  const [message, setMessage]       = useState(null);

  // Global settings
  const [enabled,          setEnabled]          = useState(true);
  const [rotationInterval, setRotationInterval] = useState(4000);
  const [showOnMobile,     setShowOnMobile]     = useState(true);
  const [showOnDesktop,    setShowOnDesktop]    = useState(true);

  // Announcement list — each item: { text, active }
  const [announcements, setAnnouncements] = useState([]);

  // ── Auth guard ───────────────────────────────────────────────────
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  // ── Load config on mount ─────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setIsFetching(true);
        const doc = await getViewConfigByNameAdmin(CONFIG_NAME);
        applyDoc(doc);
      } catch (err) {
        // 404 means no document yet — use defaults silently
        if (!err.message?.includes('404')) {
          showMessage('error', 'Failed to load configuration');
        }
      } finally {
        setIsFetching(false);
      }
    };
    load();
  }, []);

  const applyDoc = (doc) => {
    const d = doc?.data || {};
    setEnabled(d.enabled          ?? DEFAULT_CONFIG.enabled);
    setRotationInterval(d.rotationInterval ?? DEFAULT_CONFIG.rotationInterval);
    setShowOnMobile(d.showOnMobile   ?? DEFAULT_CONFIG.showOnMobile);
    setShowOnDesktop(d.showOnDesktop  ?? DEFAULT_CONFIG.showOnDesktop);
    setAnnouncements(Array.isArray(d.announcements) ? d.announcements : []);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  // ── Announcement helpers ─────────────────────────────────────────
  const handleAddAnnouncement = () => {
    setAnnouncements(prev => [...prev, { text: '', active: true }]);
  };

  const handleAnnouncementChange = (index, field, value) => {
    setAnnouncements(prev =>
      prev.map((a, i) => i === index ? { ...a, [field]: value } : a)
    );
  };

  const handleDeleteAnnouncement = (index) => {
    setAnnouncements(prev => prev.filter((_, i) => i !== index));
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    setAnnouncements(prev => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const handleMoveDown = (index) => {
    setAnnouncements(prev => {
      if (index === prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  };

  // ── Validation ───────────────────────────────────────────────────
  const validate = () => {
    const ri = Number(rotationInterval);
    if (isNaN(ri) || ri < 1000 || ri > 30000) {
      showMessage('error', 'Rotation interval must be between 1000 and 30000 ms');
      return false;
    }
    for (let i = 0; i < announcements.length; i++) {
      if (!announcements[i].text.trim()) {
        showMessage('error', `Announcement #${i + 1} cannot be empty`);
        return false;
      }
    }
    return true;
  };

  // ── Save ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      enabled,
      rotationInterval: Number(rotationInterval),
      showOnMobile,
      showOnDesktop,
      announcements: announcements.map(a => ({
        text:   a.text.trim(),
        active: a.active ?? true,
      })),
    };

    try {
      setIsSaving(true);
      const doc = await upsertViewConfigAdmin(CONFIG_NAME, payload);
      applyDoc(doc);
      showMessage('success', 'Configuration saved successfully');

      // Broadcast to all mounted useViewConfig("ANNOUNCEMENT_BAR") instances
      window.dispatchEvent(
        new CustomEvent('viewconfig:invalidate', { detail: { name: CONFIG_NAME } })
      );
    } catch (err) {
      showMessage('error', err.message || 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="ab-admin-container">

      {/* Header */}
      <div className="ab-admin-header">
        <div>
          <h2 className="ab-admin-title">Announcement Bar</h2>
          <p className="ab-admin-subtitle">
            Manage the rotating announcement bar displayed at the top of every page.
          </p>
        </div>
        <button
          className="btn"
          onClick={() => navigate('/admin')}
          style={{ height: '45px', background: 'var(--btn-secondary-bg, #3f3f46)', color: 'var(--color-text-primary)' }}
        >
          Back to Dashboard
        </button>
      </div>

      {/* Toast Message */}
      {message && (
        <div className={`ab-admin-message ab-admin-message--${message.type}`}>
          {message.text}
        </div>
      )}

      {isFetching ? (
        <div className="ab-admin-loading">Loading configuration...</div>
      ) : (
        <>
          {/* ── Settings Card ─────────────────────────────────────── */}
          <div className="ab-admin-card">
            <h3 className="ab-admin-card-title">Global Settings</h3>

            <div className="ab-admin-settings-grid">
              

              <div>
                {/* Rotation Interval */}
                <div className="ab-admin-field">
                  <label className="ab-admin-label" htmlFor="ab-interval">
                    Rotation Interval (ms)
                    <span className="ab-admin-hint"> 1000 – 30000</span>
                  </label>
                  <input
                    id="ab-interval"
                    type="number"
                    min="1000"
                    max="30000"
                    step="500"
                    value={rotationInterval}
                    onChange={e => setRotationInterval(e.target.value)}
                    className="ab-admin-input"
                  />
                </div>
              </div>

              {/* Enable / Disable */}
              <div className="ab-admin-field">
                <label className="ab-admin-label">Announcement Bar</label>
                <div className="ab-admin-toggle-row">
                  <button
                    type="button"
                    className={`ab-admin-toggle ${enabled ? 'ab-admin-toggle--on' : ''}`}
                    onClick={() => setEnabled(v => !v)}
                  >
                    <span className="ab-admin-toggle-knob" />
                  </button>
                  <span className="ab-admin-toggle-label">
                    {enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              {/* Show on Mobile */}
              <div className="ab-admin-field">
                <label className="ab-admin-label">Show on Mobile</label>
                <div className="ab-admin-toggle-row">
                  <button
                    type="button"
                    className={`ab-admin-toggle ${showOnMobile ? 'ab-admin-toggle--on' : ''}`}
                    onClick={() => setShowOnMobile(v => !v)}
                  >
                    <span className="ab-admin-toggle-knob" />
                  </button>
                  <span className="ab-admin-toggle-label">
                    {showOnMobile ? 'Visible' : 'Hidden'}
                  </span>
                </div>
              </div>

              {/* Show on Desktop */}
              <div className="ab-admin-field">
                <label className="ab-admin-label">Show on Desktop</label>
                <div className="ab-admin-toggle-row">
                  <button
                    type="button"
                    className={`ab-admin-toggle ${showOnDesktop ? 'ab-admin-toggle--on' : ''}`}
                    onClick={() => setShowOnDesktop(v => !v)}
                  >
                    <span className="ab-admin-toggle-knob" />
                  </button>
                  <span className="ab-admin-toggle-label">
                    {showOnDesktop ? 'Visible' : 'Hidden'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Announcements Card ────────────────────────────────── */}
          <div className="ab-admin-card" style={{ marginTop: '24px' }}>
            <div className="ab-admin-card-header">
              <h3 className="ab-admin-card-title">Announcements</h3>
              <button
                type="button"
                className="btn"
                onClick={handleAddAnnouncement}
                style={{ fontSize: '0.9rem', padding: '8px 16px' }}
              >
                + Add Announcement
              </button>
            </div>

            {announcements.length === 0 ? (
              <div className="ab-admin-empty">
                No announcements yet. Click "+ Add Announcement" to create one.
              </div>
            ) : (
              <div className="ab-admin-list">
                {announcements.map((ann, index) => (
                  <div key={index} className={`ab-admin-row ${!ann.active ? 'ab-admin-row--inactive' : ''}`}>
                    {/* Order controls */}
                    <div className="ab-admin-order-btns">
                      <button
                        type="button"
                        className="ab-admin-order-btn"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                        title="Move up"
                      >↑</button>
                      <button
                        type="button"
                        className="ab-admin-order-btn"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === announcements.length - 1}
                        title="Move down"
                      >↓</button>
                    </div>

                    {/* Index badge */}
                    <span className="ab-admin-index">{index + 1}</span>

                    {/* Text input */}
                    <input
                      type="text"
                      value={ann.text}
                      onChange={e => handleAnnouncementChange(index, 'text', e.target.value)}
                      placeholder="Announcement text..."
                      className="ab-admin-input ab-admin-row-input"
                    />

                    {/* Active toggle */}
                    <button
                      type="button"
                      className={`ab-admin-toggle ${ann.active ? 'ab-admin-toggle--on' : ''}`}
                      onClick={() => handleAnnouncementChange(index, 'active', !ann.active)}
                      title={ann.active ? 'Disable' : 'Enable'}
                    >
                      <span className="ab-admin-toggle-knob" />
                    </button>

                    {/* Active label */}
                    <span className={`ab-admin-status-badge ${ann.active ? 'ab-admin-status-badge--active' : 'ab-admin-status-badge--inactive'}`}>
                      {ann.active ? 'Active' : 'Inactive'}
                    </span>

                    {/* Delete */}
                    <button
                      type="button"
                      className="ab-admin-delete-btn"
                      onClick={() => handleDeleteAnnouncement(index)}
                      title="Delete"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Save Button ───────────────────────────────────────── */}
          <div style={{ marginTop: '28px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn"
              onClick={handleSave}
              disabled={isSaving}
              style={{ minWidth: '180px', fontSize: '1rem', padding: '12px 28px' }}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminAnnouncementBar;
